import {useMutation} from '@apollo/client'
import {Button, Modal, Spinner, Form} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {CANCEL_POLICY} from '../../../gql/mutations/policyMutation'
import {IResCancelPolicy} from '../soldTypes'
import {parseId} from '../../../helpers'
import {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {DateInput} from 'app/tfi/components/DateInput'
import moment from 'moment'

interface IModalProps {
  id: string
  isOpen: boolean
  onClose: () => void
  refetch: () => void
}

export const ModalCancelPolicy: React.FC<IModalProps> = ({isOpen, onClose, id, refetch}) => {
  const intl = useIntl()
  const errMsg = (message: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})
  const [gqlCancel, {data, loading, error}] = useMutation<IResCancelPolicy>(CANCEL_POLICY)
  // Misma sugerencia que el modal de status: la cobertura casi siempre termina a fin de
  // mes, no el día del clic. Es sugerencia, no imposición — el campo queda editable.
  const [cancelDate, setCancelDate] = useState<string>(() => moment().endOf('month').format('YYYY-MM-DD'))

  useEffect(() => {
    if (!isOpen) return
    setCancelDate(moment().endOf('month').format('YYYY-MM-DD'))
  }, [isOpen])

  useEffect(() => {
    if (error) toast.error(errMsg(error.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  useEffect(() => {
    if (!data) return

    if (data.cancelPolicy.success) {
      toast.success(
        intl.formatMessage({
          id: 'policies.detail.modal.cancel.toast.success',
          defaultMessage: 'Policy canceled successfully',
        })
      )
      refetch()
      onClose()
    } else if (data.cancelPolicy.errors && data.cancelPolicy.errors.length > 0) {
      toast.error(errMsg(data.cancelPolicy.errors[0].message))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, onClose, refetch])

  const handleDelete = () => {
    // Una poliza cancelada no puede quedar sin fecha: es el dato con el que se calcula el
    // tiempo activo. El backend rechaza la mutacion sin fecha; esto evita el viaje.
    if (!cancelDate) {
      toast.error(
        intl.formatMessage({
          id: 'policies.detail.statuses.canceledDateRequired',
          defaultMessage: 'The cancellation date is required to set a canceled status',
        })
      )
      return
    }

    gqlCancel({variables: {id: parseId(id), cancellationDate: cancelDate}})
  }

  return (
    <Modal show={isOpen} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage
            id='policies.detail.modal.cancel.title'
            defaultMessage='Mark this policy as canceled?'
          />
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className='d-flex align-items-start mb-3'>
          <i className='bi bi-exclamation-triangle-fill text-danger fs-2 me-3'></i>
          <div>
            <p className='fw-semibold'>
              <FormattedMessage
                id='policies.detail.modal.cancel.body'
                defaultMessage="Clicking <b>Yes, cancel it!</b> will set the status of the policy as <b>canceled</b> and include it in the following commissions report as a canceled policy."
                values={{b: (chunks) => <strong>{chunks}</strong>}}
              />
            </p>
          </div>
        </div>

        <Form.Group>
          <Form.Label className='fw-bold'>
            <FormattedMessage
              id='policies.detail.modal.cancel.dateLabel'
              defaultMessage='Cancellation date'
            /> <span className='text-muted'>
              <FormattedMessage
                id='policies.detail.modal.cancel.dateOptional'
                defaultMessage='(suggested: end of the current month)'
              />
            </span>
          </Form.Label>
          <DateInput
            id="cancelDate"
            name="cancelDate"
            value={cancelDate}
            onChange={(_field: string, val: string | null) => setCancelDate(val || '')}
            required
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
        </Button>
        <Button variant='danger' onClick={handleDelete} disabled={loading}>
          {loading && <Spinner animation='border' size='sm' className='me-2' />} <FormattedMessage
            id='policies.detail.modal.cancel.confirm'
            defaultMessage='Yes, cancel it!'
          />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
